import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { LoadingService } from '@app/shared/services/loading.service';
import { AuthService } from '../services/auth.service';
import { environment } from '@environments/environment';
import * as _ from 'lodash';
import { AccessKeyService } from '../services/access-key.service';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {

  constructor(
    private authService: AuthService,
    private accessKeyService: AccessKeyService,
    private loadingService: LoadingService,
    private messageService: MessageService,
    private router: Router
  ) { }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<IHttpResult>> {

    if (!req.headers.get('Skip-Load')) {
      this.loadingService.loading(true);
    }

    const isAccessKeyTokenReq = req.url.includes('/jurisdicionados/token')
      || req.url.includes('/jurisdicionados/comunicacoes/token');
    const accessToken = isAccessKeyTokenReq ? null : this.authService.getAccessToken();
    const isAuthReq = req.headers.get('x-www-form-urlencoded');
    const isFormDataRequest = req.headers.get('Form-Data');

    const jurisdicionado = !isAccessKeyTokenReq
      && (req.url.includes('/jurisdicionados/responder') || req.url.includes('/jurisdicionados/comunicacoes'));
    const jwtToken = this.accessKeyService.getJwtToken();
    const dashboards = req.url.includes('/dashboard/');

    let headers = accessToken ? new HttpHeaders({
      Authorization: `Bearer ${accessToken}`,
      ... (isFormDataRequest) ? {} : { 'Content-Type': isAuthReq ? 'application/x-www-form-urlencoded' : 'application/json' }
    }) : new HttpHeaders({
      ... (isFormDataRequest) ? {} : { 'Content-Type': isAuthReq ? 'application/x-www-form-urlencoded' : 'application/json' }
    });

    if (jurisdicionado && jwtToken) {
      headers = new HttpHeaders({
        'jwt-token': `${jwtToken}`,
        ... (isFormDataRequest) ? {} : { 'Content-Type': 'application/json' }
      });
    }

    if (dashboards) {
      headers = accessToken ? new HttpHeaders({
        'jwt-token': `Bearer ${accessToken}`,
        ... (isFormDataRequest) ? {} : { 'Content-Type': 'application/json' }
      }) : new HttpHeaders({
        ... (isFormDataRequest) ? {} : { 'Content-Type': 'application/json' }
      });
    }

    req = req.clone({ headers });
    return next.handle(req).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const cloneEvent = event.clone({
            body: {
              success: true,
              data: event.body || [],
              error: undefined
            }
          });

          return cloneEvent;
        }
      }),

      catchError((error: HttpErrorResponse) => {
        if (error.status === 422) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: error.error.erro
          });
        } else if (error.status === 400) {
          if (isAuthReq) {
            this.router.navigate(['/login']);
            return;
          }
          const messages = [];
          if (Array.isArray(error.error)) {
            error.error.forEach(m => {
              messages.push(`${_.get(m, 'propriedade', '').toUpperCase()} ${m.erro}`);
            });
          } else {
            messages.push(
              _.get(error, 'error.erro', 'Não conseguimos processar sua solicitação')
            );
          }
          this.messageService.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: messages.join('<br />')
          });
        } else if (isAuthReq && error) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: 'Credenciais inválidas'
          });
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: 'Houve um erro ao processar sua solicitação'
          });
        }

        return throwError({
          success: false,
          data: [{ ...error }],
          error: error.message
        });
      }),

      finalize(() => {
        this.loadingService.loading(false);
      })
    );
  }
}

interface IHttpResult {
  success: boolean;
  data: any;
  error: any;
}
